import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';
import OneSignal from 'onesignal-cordova-plugin';
import { LogLevel } from 'onesignal-cordova-plugin';

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID;

export const initializeOneSignal = async (userId?: string, requestPermission: boolean = false): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    logger.debug('OneSignal native: Not on native platform, skipping initialization');
    return;
  }

  if (!ONESIGNAL_APP_ID) {
    logger.error('OneSignal native: VITE_ONESIGNAL_APP_ID is not set in environment variables');
    return;
  }

  try {
    logger.info('OneSignal native: Enabling verbose logging');
    OneSignal.Debug.setLogLevel(LogLevel.Verbose);

    logger.info('OneSignal native: Initializing with App ID:', ONESIGNAL_APP_ID);
    OneSignal.initialize(ONESIGNAL_APP_ID);

    if (userId) {
      logger.info('OneSignal native: Logging in user with external ID:', userId);
      OneSignal.login(userId);
      logger.info('OneSignal native: User logged in successfully');
    }

    if (requestPermission) {
      logger.info('OneSignal native: Requesting notification permission');
      OneSignal.Notifications.requestPermission(false).then((accepted: boolean) => {
        logger.info('OneSignal native: User accepted notifications:', accepted);
      }).catch((error) => {
        logger.error('OneSignal native: Error requesting permission:', error);
      });
    }

    logger.info('OneSignal native: Initialization complete');
  } catch (error) {
    logger.error('OneSignal native: Error during initialization:', error);
  }
};

export const setOneSignalExternalUserId = async (userId: string): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    logger.debug('OneSignal native: Not on native platform, skipping login');
    return;
  }

  try {
    logger.info('OneSignal native: Logging in user with external ID:', userId);
    OneSignal.login(userId);
    logger.info('OneSignal native: User logged in successfully');
  } catch (error) {
    logger.error('OneSignal native: Error logging in user:', error);
  }
};

export const removeOneSignalExternalUserId = async (): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    logger.debug('OneSignal native: Not on native platform, skipping logout');
    return;
  }

  try {
    logger.info('OneSignal native: Logging out user');
    OneSignal.logout();
    logger.info('OneSignal native: User logged out successfully');
  } catch (error) {
    logger.error('OneSignal native: Error logging out user:', error);
  }
};

export const requestOneSignalPushPermission = async (fallbackToSettings: boolean = false): Promise<boolean> => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  try {
    const accepted = await OneSignal.Notifications.requestPermission(fallbackToSettings);
    logger.info('OneSignal native: Request permission result:', accepted);
    return accepted;
  } catch (error) {
    logger.error('OneSignal native: Error requesting push permission:', error);
    return false;
  }
};

